import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Building, DollarSign, Clock, Check, Loader2, Upload, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobDetailPage = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    full_name: "", email: "", phone: "", linkedin_url: "",
    current_role: "", current_company: "", years_experience: "", cover_letter: ""
  });

  const CURRENCY_COUNTRY = { USD: "US", GBP: "GB", AED: "AE" };
  // Generate JobPosting schema for SEO
  const getJobSchema = (job) => ({
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    "title": job.title,
    "description": [job.company_description, job.description, ...(job.responsibilities || []).map(r => "• " + r), job.applicant_intro, ...(job.requirements || []).map(r => "• " + r)].filter(Boolean).join("\n"),
    "datePosted": job.created_at,
    "employmentType": job.job_type === "Full-time" ? "FULL_TIME" : "CONTRACTOR",
    "hiringOrganization": {
      "@type": "Organization",
      "name": job.is_confidential ? "Confidential (via Magnus Kor Talent Partners)" : job.company,
      "sameAs": "https://magnuskor.com"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location?.split(",")[0] || "",
        "addressRegion": job.location?.split(",")[1]?.trim() || "",
        "addressCountry": CURRENCY_COUNTRY[job.salary_currency] || "US"
      }
    },
    "baseSalary": job.salary_min ? {
      "@type": "MonetaryAmount",
      "currency": job.salary_currency || "USD",
      "value": {
        "@type": "QuantitativeValue",
        "minValue": job.salary_min,
        "maxValue": job.salary_max,
        "unitText": "YEAR"
      }
    } : undefined,
    "jobLocationType": job.location_type === "Remote" ? "TELECOMMUTE" : undefined
  });

  useEffect(() => {
    axios.get(`${API}/jobs/${jobId}`)
      .then(res => setJob(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File too large (max 10MB)");
        return;
      }
      setResumeFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let resume_filename = null;
      
      // Upload resume if provided
      if (resumeFile) {
        const formDataFile = new FormData();
        formDataFile.append('file', resumeFile);
        const uploadRes = await axios.post(`${API}/upload-resume`, formDataFile);
        resume_filename = uploadRes.data.filename;
      }

      await axios.post(`${API}/applications`, {
        job_id: jobId,
        ...formData,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
        resume_filename
      });
      setSubmitted(true);
      toast.success("Application submitted!");
    } catch (error) {
      toast.error("Error submitting application");
    } finally {
      setSubmitting(false);
    }
  };

  const formatSalary = (min, max, currency = "USD") => {
    if (!min && !max) return null;
    const sym = { USD: "$", GBP: "£", AED: "AED " }[currency] || "$";
    const fmt = n => `${sym}${(n/1000).toFixed(0)}k`;
    if (min && max) return `${fmt(min)} – ${fmt(max)}`;
    return min ? `${fmt(min)}+` : `Up to ${fmt(max)}`;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#B8860B]" size={32} /></div>;
  if (!job) return <div className="min-h-screen flex items-center justify-center"><p>Job not found</p></div>;

  return (
    <div className="min-h-screen pt-24 bg-[#FAF9F6]">
      <SEO 
        title={`${job.title} at ${job.is_confidential ? "Confidential Company" : job.company}`}
        description={`${job.title} position at ${job.is_confidential ? "a confidential VC-backed startup" : job.company}. ${job.location} · ${job.seniority} level. ${job.description?.slice(0, 150)}...`}
        keywords={`${job.title} job, ${job.department} jobs, ${job.seniority} jobs, startup jobs, SaaS careers, ${job.location}`}
        canonical={`https://magnuskor.com/jobs/${job.id}`}
        jsonLd={getJobSchema(job)}
      />
      {/* Header */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-8 md:px-16">
          <Link to="/jobs" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-8">
            <ArrowLeft size={16} /> Back to roles
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-overline">{job.department}</span>
                {job.is_confidential && (
                  <span className="text-[10px] tracking-wider text-gray-400 border border-gray-200 px-2 py-0.5 uppercase">
                    Confidential
                  </span>
                )}
              </div>
              <h1 className="font-display text-4xl md:text-5xl mb-4">{job.title}</h1>
              <p className="text-lg text-gray-600">
                Our client · <span className="text-gray-400">confidential — represented by Magnus Kor Talent Partners</span>
              </p>
              {job.highlights?.length > 0 && (
                <ul className="mt-5 space-y-2" data-testid="job-highlights">
                  {job.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700">
                      <Check size={16} className="text-[#B8860B] mt-1 flex-shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}
              {job.reference && (
                <p className="text-xs text-gray-400 mt-4 tracking-wide" data-testid="job-reference">Ref: {job.reference}</p>
              )}
            </div>
            <button onClick={() => setShowModal(true)} className="btn-luxury btn-luxury-gold whitespace-nowrap" data-testid="apply-btn">
              Apply Now
            </button>
          </div>

          <div className="flex flex-wrap gap-8 mt-10 pt-10 border-t border-gray-100 text-gray-600">
            <span className="flex items-center gap-2"><MapPin size={18} className="text-gray-400" />{job.location}</span>
            <span className="flex items-center gap-2"><Building size={18} className="text-gray-400" />{job.location_type} · {job.job_type}</span>
            {formatSalary(job.salary_min, job.salary_max, job.salary_currency) && (
              <span className="flex items-center gap-2"><DollarSign size={18} className="text-gray-400" />{formatSalary(job.salary_min, job.salary_max, job.salary_currency)}</span>
            )}
            <span className="flex items-center gap-2"><Clock size={18} className="text-gray-400" />{job.seniority} level</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">
              {job.company_description && (
                <div data-testid="about-client">
                  <h2 className="font-display text-2xl mb-6">About Our Client</h2>
                  <p className="text-body whitespace-pre-line">{job.company_description}</p>
                </div>
              )}
              <div>
                <h2 className="font-display text-2xl mb-6">Job Description</h2>
                <p className="text-body whitespace-pre-line mb-6">{job.description}</p>
                {job.responsibilities?.length > 0 && (
                  <ul className="space-y-3" data-testid="job-responsibilities">
                    {job.responsibilities.map((r, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check size={18} className="text-[#B8860B] mt-1 flex-shrink-0" />
                        <span className="text-gray-600">{r}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h2 className="font-display text-2xl mb-6">The Successful Applicant</h2>
                {job.applicant_intro && (
                  <p className="text-body whitespace-pre-line mb-6" data-testid="applicant-intro">{job.applicant_intro}</p>
                )}
                <ul className="space-y-3">
                  {job.requirements.map((req, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={18} className="text-[#B8860B] mt-1 flex-shrink-0" />
                      <span className="text-gray-600">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {job.benefits?.length > 0 && (
                <div>
                  <h2 className="font-display text-2xl mb-6">What's on Offer</h2>
                  {job.offer_intro && (
                    <p className="text-body whitespace-pre-line mb-6" data-testid="offer-intro">{job.offer_intro}</p>
                  )}
                  <div className="flex flex-wrap gap-3">
                    {job.benefits.map((b, i) => (
                      <span key={i} className="px-4 py-2 bg-white border border-gray-200 text-sm text-gray-600">{b}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="bg-white p-8 border border-gray-100 sticky top-32">
                <h3 className="font-display text-xl mb-4">Interested?</h3>
                <p className="text-sm text-gray-500 mb-6">We'll review your application within 48 hours.</p>
                <button onClick={() => setShowModal(true)} className="btn-luxury btn-luxury-dark w-full">Apply Now</button>
                <p className="text-xs text-gray-400 text-center mt-4">
                  Or email <a href="mailto:recruitment@magnuskor.com" className="text-[#B8860B]">recruitment@magnuskor.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-display text-2xl">Apply for {job.title}</h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-[#B8860B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check size={32} className="text-[#B8860B]" />
                  </div>
                  <h3 className="font-display text-2xl mb-2">Application Received</h3>
                  <p className="text-gray-500 mb-8">We'll be in touch within 48 hours.</p>
                  <button onClick={() => setShowModal(false)} className="btn-luxury btn-luxury-outline">Close</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="application-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm text-gray-600 block mb-2">Full Name *</label>
                      <input type="text" required value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} className="input-luxury border-b border-gray-200" data-testid="app-name" />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 block mb-2">Email *</label>
                      <input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="input-luxury border-b border-gray-200" data-testid="app-email" />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 block mb-2">Phone</label>
                      <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="input-luxury border-b border-gray-200" />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 block mb-2">LinkedIn</label>
                      <input type="url" value={formData.linkedin_url} onChange={e => setFormData({...formData, linkedin_url: e.target.value})} className="input-luxury border-b border-gray-200" placeholder="https://linkedin.com/in/..." />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 block mb-2">Current Role</label>
                      <input type="text" value={formData.current_role} onChange={e => setFormData({...formData, current_role: e.target.value})} className="input-luxury border-b border-gray-200" />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 block mb-2">Years Experience</label>
                      <input type="number" value={formData.years_experience} onChange={e => setFormData({...formData, years_experience: e.target.value})} className="input-luxury border-b border-gray-200" />
                    </div>
                  </div>

                  {/* Resume Upload */}
                  <div>
                    <label className="text-sm text-gray-600 block mb-2">Resume (PDF, DOC, DOCX)</label>
                    <div className="border border-dashed border-gray-300 p-6 text-center">
                      {resumeFile ? (
                        <div className="flex items-center justify-center gap-3">
                          <span className="text-sm">{resumeFile.name}</span>
                          <button type="button" onClick={() => setResumeFile(null)} className="text-gray-400 hover:text-red-500">
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} className="hidden" data-testid="resume-upload" />
                          <div className="flex flex-col items-center gap-2 text-gray-400">
                            <Upload size={24} />
                            <span className="text-sm">Click to upload resume</span>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-gray-600 block mb-2">Why are you interested?</label>
                    <textarea rows={4} value={formData.cover_letter} onChange={e => setFormData({...formData, cover_letter: e.target.value})} className="input-luxury border border-gray-200 p-4 resize-none w-full" data-testid="app-cover" />
                  </div>

                  <button type="submit" disabled={submitting} className="btn-luxury btn-luxury-dark w-full" data-testid="app-submit">
                    {submitting ? <Loader2 size={18} className="animate-spin" /> : "Submit Application"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailPage;
